import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Turnstile from "@/components/Turnstile";
import { useSeo } from "@/lib/useSeo";
import {
  DONATION_ITEMS,
  FONTS,
  PHONE_ORDER_NUMBER,
  PHONE_ORDER_TEL,
  formatPrice,
  gridLimitsFor,
  type DonationItem,
  type FontChoice,
  type ItemType,
} from "@/lib/donation-items";

interface FormState {
  with_logo: boolean;
  local_number: string;
  font: FontChoice | "";
  engraving_lines: string[];
  design_description: string;
  plaque_name: string;
  purchaser_name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  contact_phone: string;
  contact_email: string;
  ibew_affiliation: string;
  custom_logo_requested: boolean;
  website: string;          // honeypot
}

const emptyForm: FormState = {
  with_logo: false,
  local_number: "",
  font: "",
  engraving_lines: [],
  design_description: "",
  plaque_name: "",
  purchaser_name: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  contact_phone: "",
  contact_email: "",
  ibew_affiliation: "",
  custom_logo_requested: false,
  website: "",
};

export default function Donate() {
  useSeo({
    title: "Donate · Henry Miller Museum",
    description:
      "Support the Henry Miller Museum: engraved bricks, granite pavers, benches, plaques, signs, and medallions installed at the IBEW founding site in St. Louis.",
  });
  const navigate = useNavigate();
  const siteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? "";

  const [selectedId, setSelectedId] = useState<ItemType | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected: DonationItem | null = useMemo(
    () => DONATION_ITEMS.find((i) => i.id === selectedId) ?? null,
    [selectedId],
  );

  function selectItem(id: ItemType) {
    const item = DONATION_ITEMS.find((i) => i.id === id);
    if (item?.phoneOrderOnly) return;     // Coins handled in their own section; no form to open.
    setSelectedId(id);
    setError(null);
    setForm((f) => {
      const limits = item ? gridLimitsFor(item, f.with_logo) : null;
      return {
        ...f,
        engraving_lines: item?.engraving === "grid"
          ? Array(limits ? limits.lines : 0).fill("")
          : [],
        design_description: "",
        plaque_name: "",
        font: item?.supportsFont ? (f.font || "arial") : "",
        with_logo: item?.supportsLogo ? f.with_logo : false,
        local_number: item?.supportsLogo ? f.local_number : "",
      };
    });
    setTimeout(() => {
      document.getElementById("order-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  // ── Availability inquiry state ──
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiry, setInquiry] = useState({
    item_id: "any" as ItemType | "any",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    note: "",
    website: "",
  });
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [inquiryError, setInquiryError] = useState<string | null>(null);
  const [inquirySent, setInquirySent] = useState(false);

  async function onInquirySubmit(e: React.FormEvent) {
    e.preventDefault();
    setInquiryError(null);
    setInquirySubmitting(true);
    try {
      await api.inquireAvailability({
        item_id: inquiry.item_id,
        contact_name: inquiry.contact_name,
        contact_email: inquiry.contact_email,
        contact_phone: inquiry.contact_phone || undefined,
        note: inquiry.note || undefined,
        website: inquiry.website,
      });
      setInquirySent(true);
    } catch (err) {
      setInquiryError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setInquirySubmitting(false);
    }
  }

  function setLogo(withLogo: boolean) {
    if (!selected) return;
    setForm((f) => {
      const limits = gridLimitsFor(selected, withLogo);
      const newLines = selected.engraving === "grid"
        ? Array(limits ? limits.lines : 0).fill("").map((_, i) => f.engraving_lines[i] ?? "")
        : f.engraving_lines;
      // Truncate per-line characters to new width if needed.
      const cap = limits?.charsPerLine ?? Infinity;
      return {
        ...f,
        with_logo: withLogo,
        engraving_lines: newLines.map((l) => l.slice(0, cap)),
        local_number: withLogo ? f.local_number : "",
      };
    });
  }

  function setLine(idx: number, value: string, maxChars: number) {
    const trimmed = value.slice(0, maxChars);
    setForm((f) => {
      const copy = [...f.engraving_lines];
      copy[idx] = trimmed;
      return { ...f, engraving_lines: copy };
    });
  }

  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        item_id: selected.id,
        with_logo: form.with_logo,
        local_number: form.with_logo ? form.local_number : "",
        font: selected.supportsFont ? form.font || undefined : undefined,
        engraving_lines: selected.engraving === "grid"
          ? form.engraving_lines.map((l) => l ?? "")
          : undefined,
        design_description: selected.engraving === "design" ? form.design_description : undefined,
        plaque_name: selected.engraving === "name" ? form.plaque_name : undefined,
        purchaser_name: form.purchaser_name,
        street: form.street,
        city: form.city,
        state: form.state,
        zip: form.zip,
        contact_phone: form.contact_phone,
        contact_email: form.contact_email,
        ibew_affiliation: form.ibew_affiliation || undefined,
        custom_logo_requested: selected.supportsCustomLogo ? form.custom_logo_requested : false,
        turnstile_token: turnstileToken || undefined,
        website: form.website,
      };
      const r = await api.submitOrder(payload);
      navigate(`/thank-you?order=${r.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* ─────────── HERO ─────────── */}
      <section className="border-b border-museum-line bg-museum-paper">
        <div className="max-w-6xl mx-auto px-5 pt-12 pb-10 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-eyebrow text-museum-accent2 font-semibold mb-3">
              Support the museum
            </div>
            <h1 className="font-serif text-[40px] sm:text-[52px] leading-[1.05] text-museum-ink">
              Leave your mark on the <em className="not-italic text-museum-accent">birthplace of the IBEW</em>.
            </h1>
            <div className="rule-gold mt-5" />
            <p className="mt-4 text-museum-muted leading-relaxed">
              Engraved bricks, granite pavers, benches, plaques, signs, and medallions
              installed at the Henry Miller Museum in St. Louis honor brothers and sisters
              past and present, and fund the continued restoration and operation of the
              building where the IBEW was founded in 1891. Every contribution goes directly
              to the Electrical Workers Historical Society, a 501(c)(3).
            </p>
          </div>
          <div className="relative">
            <div className="absolute inset-0 -m-2 border border-museum-rule/60 rounded-sm pointer-events-none" />
            <img
              src="/donations/medallion-309.jpg"
              alt="Carved limestone medallion honoring IBEW Local 309 of Collinsville, IL, installed at the Henry Miller Museum."
              width={1600}
              height={1200}
              className="block w-full rounded-sm bg-museum-paperDeep object-cover aspect-[4/3]"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* ─────────── AVAILABILITY INQUIRY ─────────── */}
      <section className="max-w-6xl mx-auto px-5 pt-8">
        <div className="card-warm p-5 sm:p-6">
          {!inquiryOpen && !inquirySent && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="section-eyebrow">Limited inventory</div>
                <p className="text-sm text-museum-ink leading-relaxed">
                  Some items (pavers, benches, light poles, signs, medallions) exist in
                  limited numbers on the museum grounds. Check availability before designing
                  your order.
                </p>
              </div>
              <button type="button" className="btn-secondary shrink-0"
                      onClick={() => setInquiryOpen(true)}>
                Check availability →
              </button>
            </div>
          )}
          {inquirySent && (
            <div>
              <div className="section-eyebrow">Inquiry sent</div>
              <p className="text-sm text-museum-ink leading-relaxed">
                Thanks. Jennifer Edgar at IBEW Local 1 will follow up by email, usually
                within five business days.
              </p>
            </div>
          )}
          {inquiryOpen && !inquirySent && (
            <form onSubmit={onInquirySubmit} className="space-y-4" noValidate>
              <div>
                <div className="section-eyebrow">Check availability</div>
                <p className="text-sm text-museum-muted leading-relaxed">
                  No commitment. We'll just let you know what's still available.
                </p>
              </div>
              {/* honeypot */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off"
                     value={inquiry.website}
                     onChange={(e) => setInquiry((q) => ({ ...q, website: e.target.value }))}
                     className="hidden" aria-hidden />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="inq_item">Item you're interested in</label>
                  <select id="inq_item" className="select"
                          value={inquiry.item_id}
                          onChange={(e) => setInquiry((q) => ({ ...q, item_id: e.target.value as ItemType | "any" }))}>
                    <option value="any">Not sure / just exploring</option>
                    {DONATION_ITEMS.filter((i) => !i.phoneOrderOnly).map((i) => (
                      <option key={i.id} value={i.id}>{i.name} ({i.priceLabel})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor="inq_name">Your name</label>
                  <input id="inq_name" required className="input"
                         value={inquiry.contact_name}
                         onChange={(e) => setInquiry((q) => ({ ...q, contact_name: e.target.value }))} />
                </div>
                <div>
                  <label className="label" htmlFor="inq_email">Email</label>
                  <input id="inq_email" type="email" required className="input"
                         value={inquiry.contact_email}
                         onChange={(e) => setInquiry((q) => ({ ...q, contact_email: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="inq_phone">
                    Phone <span className="text-museum-muted/80 normal-case font-normal tracking-normal">(optional)</span>
                  </label>
                  <input id="inq_phone" type="tel" inputMode="tel" autoComplete="tel" className="input"
                         value={inquiry.contact_phone}
                         onChange={(e) => setInquiry((q) => ({ ...q, contact_phone: e.target.value }))} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="inq_note">
                    Anything we should know? <span className="text-museum-muted/80 normal-case font-normal tracking-normal">(optional)</span>
                  </label>
                  <textarea id="inq_note" className="textarea"
                            value={inquiry.note}
                            onChange={(e) => setInquiry((q) => ({ ...q, note: e.target.value }))} />
                </div>
              </div>
              {inquiryError && (
                <div className="text-sm bg-red-50 border border-museum-accent/30 text-museum-accent rounded-sm p-3">
                  {inquiryError}
                </div>
              )}
              <div className="flex items-center justify-between gap-3 pt-1">
                <button type="button" className="btn-ghost"
                        onClick={() => setInquiryOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={inquirySubmitting}>
                  {inquirySubmitting ? "Sending…" : "Send inquiry →"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ─────────── EDITORIAL LIST ─────────── */}
      <section className="max-w-4xl mx-auto px-5 py-10">
        <div className="section-eyebrow">Ways to give</div>
        <h2 className="font-serif text-[28px] sm:text-[34px] leading-tight text-museum-ink">
          Sponsor with an engraving
        </h2>
        <div className="rule-gold" />
        <p className="text-museum-muted leading-relaxed max-w-prose">
          Every contribution funds the continued restoration and operation of the
          building where the IBEW was founded in 1891. Choose an item below to begin
          your order. We'll follow up by email to confirm the details before payment.
        </p>

        <ol className="mt-10 divide-y divide-museum-line/80 border-t border-museum-line/80">
          {DONATION_ITEMS.filter((i) => !i.phoneOrderOnly).map((item) => (
            <li key={item.id}>
              <article className="py-7 grid sm:grid-cols-[1fr_auto] gap-5 items-start">
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <h3 className="font-serif text-[20px] sm:text-[22px] text-museum-ink">
                      {item.name}
                    </h3>
                    <div className="text-museum-accent2 text-sm font-medium tabular-nums">
                      {item.priceLabel}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-museum-ink/85 leading-relaxed max-w-prose">
                    {item.blurb}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => selectItem(item.id)}
                  className={
                    "shrink-0 sm:self-center " +
                    (selectedId === item.id ? "btn-primary" : "btn-secondary")
                  }
                >
                  {selectedId === item.id ? "Selected ↓" : "Choose this →"}
                </button>
              </article>
            </li>
          ))}
        </ol>

        <p className="mt-8 text-sm text-museum-muted">
          <span className="text-museum-ink">See more examples →</span>{" "}
          {/* TODO: Replace with the Flickr / gallery links provided by the museum. */}
          gallery links coming soon.
        </p>
      </section>

      {/* ─────────── ORDER FORM ─────────── */}
      {selected && (
        <section id="order-form" className="max-w-4xl mx-auto px-5 pb-16">
          <form className="card p-7 sm:p-9 space-y-7" onSubmit={onSubmit} noValidate>
            <header>
              <div className="section-eyebrow">Order details</div>
              <h2 className="section-title">{selected.name} ({formatPrice(selected.priceCents)})</h2>
              <div className="rule-gold" />
              <p className="text-sm text-museum-muted leading-relaxed">{selected.description}</p>
            </header>

            {/* honeypot */}
            <input type="text" name="website" tabIndex={-1} autoComplete="off"
                   value={form.website} onChange={(e) => set("website", e.target.value)}
                   className="hidden" aria-hidden />

            {/* ── Logo + font ── */}
            {(selected.supportsLogo || selected.supportsFont) && (
              <div className="form-group">
                <h3 className="form-group-title">Style</h3>

                {selected.supportsLogo && (
                  <div>
                    <label className="label">IBEW logo</label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setLogo(false)}
                        className={"text-left p-3 rounded-sm border transition " +
                          (!form.with_logo
                            ? "border-museum-accent bg-museum-paper"
                            : "border-museum-line bg-museum-paper hover:bg-museum-paperDeep")}
                      >
                        <strong className="block text-sm">Without logo</strong>
                        <span className="text-sm text-museum-muted">
                          More room for engraved text.
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setLogo(true)}
                        className={"text-left p-3 rounded-sm border transition " +
                          (form.with_logo
                            ? "border-museum-accent bg-museum-paper"
                            : "border-museum-line bg-museum-paper hover:bg-museum-paperDeep")}
                      >
                        <strong className="block text-sm">With IBEW logo</strong>
                        <span className="text-sm text-museum-muted">
                          IBEW seal + Local # at the top.
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {selected.supportsLogo && form.with_logo && (
                  <div>
                    <label className="label" htmlFor="local_number">
                      IBEW Local # <span className="text-museum-accent">·</span> required for with-logo
                    </label>
                    <input
                      id="local_number"
                      required
                      className="input max-w-xs"
                      placeholder="e.g. 1, 309, 890"
                      value={form.local_number}
                      onChange={(e) => set("local_number", e.target.value)}
                    />
                  </div>
                )}

                {selected.supportsFont && (
                  <div>
                    <label className="label">Font</label>
                    <div className="grid sm:grid-cols-3 gap-2">
                      {FONTS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          onClick={() => set("font", f.id)}
                          className={"text-left p-3 rounded-sm border transition " +
                            (form.font === f.id
                              ? "border-museum-accent bg-museum-paper"
                              : "border-museum-line bg-museum-paper hover:bg-museum-paperDeep")}
                          style={{ fontFamily: f.cssFamily }}
                        >
                          <strong className="block text-sm">{f.label}</strong>
                          <span className="text-base text-museum-ink">The quick brown fox</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Engraving ── */}
            <div className="form-group">
              <h3 className="form-group-title">Engraving</h3>
              {selected.engravingHelp && (
                <p className="text-sm text-museum-muted leading-relaxed">{selected.engravingHelp}</p>
              )}

              {selected.engraving === "grid" && (
                <EngravingGrid
                  item={selected}
                  withLogo={form.with_logo}
                  lines={form.engraving_lines}
                  font={form.font}
                  onChange={(i, v, max) => setLine(i, v, max)}
                />
              )}

              {selected.engraving === "design" && (
                <div>
                  <label className="label" htmlFor="design_description">
                    Describe your engraving / design
                  </label>
                  <textarea
                    id="design_description"
                    required
                    className="textarea min-h-[160px]"
                    placeholder={selected.designPlaceholder}
                    value={form.design_description}
                    onChange={(e) => set("design_description", e.target.value)}
                  />
                </div>
              )}

              {selected.engraving === "name" && (
                <div>
                  <label className="label" htmlFor="plaque_name">
                    Name for the plaque
                  </label>
                  <input
                    id="plaque_name"
                    required
                    className="input"
                    placeholder='e.g. "In memory of John Doe" or "IBEW Local 1"'
                    value={form.plaque_name}
                    onChange={(e) => set("plaque_name", e.target.value)}
                  />
                </div>
              )}
            </div>

            {/* ── Purchaser ── */}
            <div className="form-group">
              <h3 className="form-group-title">Your information</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="purchaser_name">Name (as it should appear on the order)</label>
                  <input id="purchaser_name" required className="input"
                         value={form.purchaser_name}
                         onChange={(e) => set("purchaser_name", e.target.value)} />
                </div>
                <div>
                  <label className="label" htmlFor="contact_email">Email</label>
                  <input id="contact_email" type="email" required className="input"
                         value={form.contact_email}
                         onChange={(e) => set("contact_email", e.target.value)} />
                </div>
                <div>
                  <label className="label" htmlFor="contact_phone">Phone</label>
                  <input id="contact_phone" type="tel" required inputMode="tel" autoComplete="tel"
                         className="input"
                         value={form.contact_phone}
                         onChange={(e) => set("contact_phone", e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="label" htmlFor="street">Street address</label>
                  <input id="street" required autoComplete="street-address" className="input"
                         value={form.street}
                         onChange={(e) => set("street", e.target.value)} />
                </div>
                <div>
                  <label className="label" htmlFor="city">City</label>
                  <input id="city" required autoComplete="address-level2" className="input"
                         value={form.city}
                         onChange={(e) => set("city", e.target.value)} />
                </div>
                <div>
                  <label className="label" htmlFor="state">State / Province</label>
                  <input id="state" required autoComplete="address-level1" className="input"
                         value={form.state}
                         onChange={(e) => set("state", e.target.value)} />
                </div>
                <div>
                  <label className="label" htmlFor="zip">ZIP / Postal code</label>
                  <input id="zip" required autoComplete="postal-code" className="input"
                         value={form.zip}
                         onChange={(e) => set("zip", e.target.value)} />
                </div>
                <div>
                  <label className="label" htmlFor="ibew_affiliation">
                    IBEW affiliation <span className="text-museum-muted/80 normal-case font-normal tracking-normal">(optional)</span>
                  </label>
                  <input id="ibew_affiliation" className="input"
                         placeholder="Local number, role, or leave blank"
                         value={form.ibew_affiliation}
                         onChange={(e) => set("ibew_affiliation", e.target.value)} />
                </div>
              </div>
            </div>

            {/* ── Custom logo opt-in ── */}
            {selected.supportsCustomLogo && (
              <div className="form-group">
                <h3 className="form-group-title">Custom logo (optional)</h3>
                <p className="text-sm text-museum-muted leading-relaxed -mt-1">
                  Want your own logo on this piece instead of (or alongside) the IBEW seal?
                  Local crests, family marks, and trade-school logos are all welcome.
                </p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 accent-museum-accent"
                    checked={form.custom_logo_requested}
                    onChange={(e) => set("custom_logo_requested", e.target.checked)}
                  />
                  <span className="text-sm leading-snug text-museum-ink">
                    <strong className="block">I'd like to submit a custom vector logo.</strong>
                    <span className="text-museum-muted">
                      Vector format only: <strong className="text-museum-ink">.eps</strong> or <strong className="text-museum-ink">.ai</strong> (Adobe Illustrator). After you submit this form you'll get a confirmation email; reply to it with your logo file attached and Jennifer will forward it to the engraver.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* ── Summary ── */}
            <div className="bg-museum-paperDeep/60 border border-museum-line rounded-sm p-5">
              <div className="section-eyebrow">Summary</div>
              <div className="font-serif text-lg text-museum-ink">{selected.name}</div>
              <div className="text-sm text-museum-muted mt-1">
                {selected.supportsLogo ? (form.with_logo ? "With IBEW logo" : "Without logo") : null}
                {selected.supportsLogo && form.with_logo && form.local_number ? ` · Local #${form.local_number}` : ""}
                {selected.supportsFont && form.font ? ` · ${FONTS.find((f) => f.id === form.font)?.label}` : ""}
              </div>
              <div className="mt-3 text-lg text-museum-ink">
                <strong>{formatPrice(selected.priceCents)}</strong>
              </div>
              <p className="text-xs text-museum-muted mt-2">
                Submit the form below and our staff will follow up by email, typically within five business days, to confirm details before payment. EWHS is a 501(c)(3); contributions are tax-deductible to the extent allowed by law.
              </p>
            </div>

            {siteKey && <Turnstile siteKey={siteKey} onToken={setTurnstileToken} />}

            {error && (
              <div className="text-sm bg-red-50 border border-museum-accent/30 text-museum-accent rounded-sm p-3">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-4 pt-2">
              <button type="button" className="btn-ghost" onClick={() => setSelectedId(null)}>
                ← Choose a different item
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Submitting…" : "Submit order →"}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* ─────────── COMMEMORATIVE COINS ─────────── */}
      <section className="border-t border-museum-line bg-museum-paperDeep/40">
        <div className="max-w-4xl mx-auto px-5 py-14">
          <div className="section-eyebrow">Take home a piece of the history</div>
          <h2 className="font-serif text-[28px] sm:text-[34px] leading-tight text-museum-ink">
            Commemorative coins
          </h2>
          <div className="rule-gold mt-4" />
          <p className="mt-3 text-museum-muted max-w-prose leading-relaxed">
            Limited-edition coins struck for the Electrical Workers Historical Society.
            One face bears the IBEW founding seal ringed by the ten founders' names;
            the reverse shows the Henry Miller boarding house with the inscription
            <em className="not-italic"> Where It Began</em>. Available in copper and brass.
          </p>

          <ul className="mt-10 divide-y divide-museum-line/80 border-t border-museum-line/80">
            {DONATION_ITEMS.filter((i) => i.phoneOrderOnly).map((item) => (
              <li key={item.id} className="py-7 grid sm:grid-cols-[1fr_auto] gap-5 items-start">
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <h3 className="font-serif text-[20px] sm:text-[22px] text-museum-ink">
                      {item.name}
                    </h3>
                    <div className="text-museum-accent2 text-sm font-medium tabular-nums">
                      {item.priceLabel}
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-museum-ink/85 leading-relaxed max-w-prose">
                    {item.blurb}
                  </p>
                </div>
                <a
                  href={`tel:${PHONE_ORDER_TEL}`}
                  className="btn-secondary shrink-0 sm:self-center whitespace-nowrap"
                >
                  Call {PHONE_ORDER_NUMBER} →
                </a>
              </li>
            ))}
          </ul>

          <p className="mt-6 text-xs text-museum-muted">
            Coins are sold by phone or in person at the museum. Credit card or cash accepted.
          </p>
        </div>
      </section>
    </>
  );
}

// ─────────── Engraving character-cell grid ───────────

interface GridProps {
  item: DonationItem;
  withLogo: boolean;
  lines: string[];
  font: FontChoice | "";
  onChange: (i: number, value: string, max: number) => void;
}

function EngravingGrid({ item, withLogo, lines, font, onChange }: GridProps) {
  const limits = gridLimitsFor(item, withLogo);
  if (!limits) return null;
  const { charsPerLine, lines: maxLines } = limits;
  const cssFamily = FONTS.find((f) => f.id === font)?.cssFamily ?? "ui-monospace, Menlo, Consolas, monospace";

  return (
    <div>
      <p className="help">
        Up to <strong>{charsPerLine} characters per line</strong>, <strong>{maxLines} lines</strong>. Spaces and punctuation count.
      </p>
      <div className="mt-3 space-y-2">
        {Array.from({ length: maxLines }).map((_, i) => {
          const value = lines[i] ?? "";
          return (
            <div key={i}>
              <div className="flex items-baseline justify-between mb-1">
                <label className="label mb-0">Line {i + 1}</label>
                <span className="text-[11px] text-museum-muted tabular-nums">
                  {value.length} / {charsPerLine}
                </span>
              </div>
              <div className="flex gap-0" role="presentation">
                {Array.from({ length: charsPerLine }).map((_, c) => (
                  <span
                    key={c}
                    aria-hidden
                    className="inline-flex items-center justify-center border border-museum-line bg-white text-museum-ink text-base sm:text-lg select-none"
                    style={{
                      width: `calc(100% / ${charsPerLine})`,
                      aspectRatio: "1 / 1",
                      marginLeft: c === 0 ? 0 : -1,
                      fontFamily: cssFamily,
                    }}
                  >
                    {value[c] ?? ""}
                  </span>
                ))}
              </div>
              <input
                type="text"
                aria-label={`Line ${i + 1} engraving text`}
                className="input mt-1 font-mono"
                maxLength={charsPerLine}
                value={value}
                onChange={(e) => onChange(i, e.target.value, charsPerLine)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
